import { ATTEST_HISTORY_ENABLED } from '@/components/attest/flags'
import type { ProductEntitlement, Role } from '@/types'

/** The purchasable products, mirroring PURCHASABLE_PRODUCTS in
 *  apps/api/entitlements.py. `platform` is deliberately absent: it is not
 *  purchasable, every account holds it, and nothing in the nav should ever
 *  offer to sell it. */
export type ProductKey = 'qoe' | 'valuation' | 'attest' | 'otcid'

export interface NavItem {
  href: string
  label: string
  icon: string
  section?: 'workspace' | 'documents' | 'admin'
  /** Which product this item belongs to. Untagged items are `platform` —
   *  admin, compliance, firm profile — and are never greyed by entitlement. */
  product?: ProductKey
  /** Filled in by navItemsFor. Undefined on the raw role map. */
  entitled?: boolean
  /** Entitled AND this person may open it. Only `true` renders a link. */
  accessible?: boolean
  /** Why the item is not clickable. Undefined when it is. */
  disabledReason?: string
}

/** Where each product opens INSIDE this SPA.
 *
 * Deliberately not taken from the server's PRODUCT_META.path: the server names
 * the product ("/attest"), the SPA owns its own route table and the actual
 * landing route is /attest/letters. The client is the authority on client
 * routing; the server is the authority on entitlement.
 */
export const PRODUCT_HOME: Record<ProductKey, string> = {
  qoe: '/dashboard',
  valuation: '/valuation',
  attest: '/attest/letters',
  // OTCID is a separate SPA in a separate container with no route registered
  // here. It has no in-app home, so the hub must not offer one — see
  // hubProducts(), which drops it rather than guessing a cross-app URL.
  otcid: '',
}

// ATTEST-UI-1 Task 0: attest mounts inside this SPA rather than as a second
// front door — it reuses the auth, shell, and nav that already exist. Extract it
// only on concrete pain (separate release cadence, separate tenant), not now.
const ATTEST_NAV: NavItem[] = [
  { href: '/attest/letters', label: 'Verification Letters', icon: '✉', section: 'workspace', product: 'attest' },
  ...(ATTEST_HISTORY_ENABLED
    ? [{ href: '/attest/history', label: 'Letter History', icon: '⧉', section: 'workspace' as const, product: 'attest' as const }]
    : []),
]

/** The nav a role gets when its firm holds EVERY product.
 *
 * Read it as the ceiling, not the current state: entitlement can only take
 * items away (grey them — see navItemsFor), never add one. So an item's
 * absence here means the role does not get that product's nav at any
 * entitlement level, and nothing downstream should synthesize it back.
 */
export const NAV_ITEMS_BY_ROLE: Record<Role, NavItem[]> = {
  super_admin: [
    { href: '/dashboard', label: 'Dashboard', icon: '◈', section: 'workspace', product: 'qoe' },
    { href: '/quote', label: 'Quote', icon: '◑', section: 'workspace', product: 'qoe' },
    { href: '/engagements/new', label: 'New Engagement', icon: '✦', section: 'workspace', product: 'qoe' },
    { href: '/normalization', label: 'QoE Workspace', icon: '◎', section: 'workspace', product: 'qoe' },
    { href: '/reports', label: 'Report Generator', icon: '▦', section: 'workspace', product: 'qoe' },
    { href: '/scope', label: 'What We Assess', icon: '☰', section: 'workspace', product: 'qoe' },
    { href: '/valuation', label: 'Valuation', icon: '△', section: 'workspace', product: 'valuation' },
    ...ATTEST_NAV,
    { href: '/seller', label: 'Seller Portal', icon: '⊞', section: 'documents', product: 'qoe' },
    { href: '/buyer', label: 'Buyer Portal', icon: '◇', section: 'documents', product: 'qoe' },
    { href: '/admin', label: 'Admin', icon: '⊙', section: 'admin' },
    { href: '/admin/access-requests', label: 'Access Requests', icon: '◼', section: 'admin' },
    { href: '/admin/monitor', label: 'Monitor', icon: '◍', section: 'admin' },
    { href: '/admin/users', label: 'Staff', icon: '♡', section: 'admin' },
    // Untagged on purpose: no `product:` key. The screen that decides who holds
    // a product must not be greyed out by whether this account holds one.
    { href: '/admin/entitlements', label: 'Entitlements', icon: '⊟', section: 'admin' },
    // QOE-CPA-FIXPACK-1 D6: this is the SA approval queue, not where brand info
    // is entered. The CPA-facing /brand-registration page keeps its own name.
    { href: '/admin/brand-registrations', label: 'Brand Registration Approval', icon: '◈', section: 'admin' },
    { href: '/admin/scope-config', label: 'Scope & Tiers', icon: '⚖', section: 'admin' },
    { href: '/admin/compliance', label: 'Compliance', icon: '◐', section: 'admin' },
    // CPA-ONBOARDING-1: the firm's legal identity, not its look and feel.
    { href: '/firm-profile', label: 'Firm Profile', icon: '⌂', section: 'admin' },
  ],
  cpa: [
    { href: '/dashboard', label: 'Dashboard', icon: '◈', section: 'workspace', product: 'qoe' },
    { href: '/quote', label: 'Quote', icon: '◑', section: 'workspace', product: 'qoe' },
    { href: '/engagements/new', label: 'New Engagement', icon: '✦', section: 'workspace', product: 'qoe' },
    { href: '/normalization', label: 'QoE Workspace', icon: '◎', section: 'workspace', product: 'qoe' },
    { href: '/reports', label: 'Report Generator', icon: '▦', section: 'workspace', product: 'qoe' },
    { href: '/scope', label: 'What We Assess', icon: '☰', section: 'workspace', product: 'qoe' },
    { href: '/valuation', label: 'Valuation', icon: '△', section: 'workspace', product: 'valuation' },
    ...ATTEST_NAV,
    { href: '/seller', label: 'Seller Portal', icon: '⊞', section: 'documents', product: 'qoe' },
    { href: '/buyer', label: 'Buyer Portal', icon: '◇', section: 'documents', product: 'qoe' },
    { href: '/admin/compliance', label: 'Compliance', icon: '◐', section: 'admin' },
    { href: '/firm-profile', label: 'Firm Profile', icon: '⌂', section: 'admin' },
  ],
  tester: [
    { href: '/dashboard', label: 'Dashboard', icon: '◈', section: 'workspace', product: 'qoe' },
    { href: '/quote', label: 'Quote', icon: '◑', section: 'workspace', product: 'qoe' },
    { href: '/engagements/new', label: 'New Engagement', icon: '✦', section: 'workspace', product: 'qoe' },
    { href: '/normalization', label: 'QoE Workspace', icon: '◎', section: 'workspace', product: 'qoe' },
    { href: '/reports', label: 'Report Generator', icon: '▦', section: 'workspace', product: 'qoe' },
    { href: '/scope', label: 'What We Assess', icon: '☰', section: 'workspace', product: 'qoe' },
    ...ATTEST_NAV,
    { href: '/seller', label: 'Seller Portal', icon: '⊞', section: 'documents', product: 'qoe' },
    { href: '/buyer', label: 'Buyer Portal', icon: '◇', section: 'documents', product: 'qoe' },
    { href: '/admin/compliance', label: 'Compliance', icon: '◐', section: 'admin' },
    { href: '/firm-profile', label: 'Firm Profile', icon: '⌂', section: 'admin' },
  ],
  analyst: [
    { href: '/dashboard', label: 'Dashboard', icon: '◈', section: 'workspace', product: 'qoe' },
    { href: '/normalization', label: 'QoE Workspace', icon: '◎', section: 'workspace', product: 'qoe' },
  ],
  firm: [
    { href: '/dashboard', label: 'Dashboard', icon: '◈', section: 'workspace', product: 'qoe' },
    { href: '/firm-profile', label: 'Firm Profile', icon: '⌂', section: 'admin' },
  ],
  // Valuation entitlements (VALUATOR-UI-1) carry NO QoE grant — a valuation
  // role sees the Valuator and nothing else. That is the whole point of the
  // entitlement: the Valuator is licensed separately from the QoE platform.
  valuation_signer: [
    { href: '/valuation', label: 'Valuation', icon: '△', section: 'workspace', product: 'valuation' },
  ],
  valuation_owner: [
    { href: '/valuation', label: 'Valuation', icon: '△', section: 'workspace', product: 'valuation' },
  ],
  pending_approval: [],
}

/** The two ways an item can be un-openable, worded for the person reading it.
 *
 * They are different problems with different next steps and the nav must not
 * blur them: one is a purchase the firm has not made, the other is a
 * permission the firm has not granted. Telling an analyst to "buy" something
 * their partner already bought sends them to the wrong person.
 */
const NOT_ENTITLED_REASON = 'Not included in your plan'
const NOT_ACCESSIBLE_REASON = 'Your firm has this — ask your partner for access'

/** Resolve a role's nav against what the account actually holds.
 *
 * Un-entitled items are kept and marked, never dropped. Hiding is the one
 * thing this must not do: a firm that cannot see Valuation exists will never
 * ask to buy it, and the visibility IS the funnel (RBAC-1 build item 4).
 *
 * `products` undefined means we do not know yet — the claims-only identity the
 * E2E bootstrap builds from a JWT carries no entitlements. Fall back to the
 * plain role nav rather than greying everything: the server is the real gate,
 * so an over-permissive nav costs a 403 on click, while an under-permissive
 * one would grey out a working app for anyone whose /me call has not landed.
 */
export function navItemsFor(
  role: Role | string | undefined,
  products: ProductEntitlement[] | undefined,
): NavItem[] {
  const base = NAV_ITEMS_BY_ROLE[role as Role] ?? []
  if (!products) {
    return base.map(item => ({ ...item, entitled: true, accessible: true }))
  }
  const byKey = new Map(products.map(p => [p.product, p]))
  return base.map(item => {
    if (!item.product) {
      // Platform surface — admin, compliance, firm profile. Not purchasable,
      // so entitlement has no opinion; the role map already decided.
      return { ...item, entitled: true, accessible: true }
    }
    const state = byKey.get(item.product)
    // A product the payload does not mention at all is treated as held. The
    // server enumerates every purchasable product, so a gap means an older
    // payload, not a denial — and denying on a gap would blank the nav on the
    // first deploy where the two sides disagree.
    if (!state) return { ...item, entitled: true, accessible: true }
    return {
      ...item,
      entitled: state.entitled,
      accessible: state.entitled && state.accessible,
      disabledReason: !state.entitled
        ? NOT_ENTITLED_REASON
        : state.accessible
          ? undefined
          : NOT_ACCESSIBLE_REASON,
    }
  })
}

export interface HubProduct {
  product: ProductKey
  label: string
  href: string
  entitled: boolean
  accessible: boolean
}

/** The products the launcher offers, in nav order.
 *
 * Only products with a route in THIS SPA: OTCID is entitled and enforced
 * server-side, but it lives in another container with no route here, so the
 * hub would render a link to nowhere. It is omitted rather than guessed.
 */
export function hubProducts(products: ProductEntitlement[] | undefined): HubProduct[] {
  if (!products) return []
  const out: HubProduct[] = []
  for (const p of products) {
    const home = PRODUCT_HOME[p.product as ProductKey]
    if (!home) continue
    out.push({
      product: p.product as ProductKey,
      label: p.label,
      href: home,
      entitled: p.entitled,
      accessible: p.accessible,
    })
  }
  return out
}

/** The products this person can actually open. Drives the routing decision:
 *  0 -> nothing to show, 1 -> go straight in, 2+ -> the launcher. */
export function openableProducts(products: ProductEntitlement[] | undefined): HubProduct[] {
  return hubProducts(products).filter(p => p.accessible)
}
